import { type Accessor, createEffect, createMemo, createResource, createSignal, on } from 'solid-js'
import type { PageChangeEventType, PerPageChangeEventType } from '~/types/Table'

export interface ServerTableResult<T> {
  items: T[]
  meta: { total: number }
}

export interface UseServerTableOptions<T> {
  /** Recibe los filtros de la página (sin page/limit) y devuelve items + meta. */
  fetcher: (params: Record<string, string | number | undefined>) => Promise<ServerTableResult<T>>
  /** Los filtros de la página: estado, búsqueda, etc. Sin page ni limit. */
  params: () => Record<string, string | number | undefined>
  /** Filas por página iniciales. La Table puede cambiarlas desde su selector. */
  perPage?: number
}

export interface ServerTableApi<T> {
  /** Items de la página actual. */
  data: Accessor<T[]>
  /** Total de filas que hay en el backend con los filtros actuales. */
  total: Accessor<number>
  /** Cargando el recurso actual. */
  loading: Accessor<boolean>
  /** Error del último fetch, si hubo. */
  error: Accessor<unknown>
  /** Re-ejecuta el fetch con los mismos filtros (tras borrar, guardar...). */
  refetch: () => void
  /** Vuelve a la primera página (búsqueda/filtros cambian). */
  resetPage: () => void
  /** Props que el Table necesita para paginar server-side. */
  page: Accessor<number>
  perPage: Accessor<number>
  onPageChange: (event: PageChangeEventType) => void
  onPerPageChange: (event: PerPageChangeEventType) => void
}

/**
 * Paginación server-side de un listado, una sola vez y para todas las páginas.
 *
 * Antes cada listado traía `limit: 200` y la Table paginaba en cliente: la
 * consulta no coincidía con el paginador ("Filas: 15" pero pidiendo 200) y el
 * catálogo moría al crecer. Ahora el backend pagina de verdad y este hook
 * conecta los dos lados:
 *
 *   const table = useServerTable<QuoteType>({
 *     fetcher: params => quoteService.list(params as Parameters<typeof quoteService.list>[0]),
 *     params: () => ({ status: filterStatus() || undefined, title: debouncedSearch().trim() || undefined })
 *   })
 *
 *   <Table data={table.data()} total={table.total()} loading={table.loading()} serverSide
 *     page={table.page()} perPage={table.perPage()} onPageChange={table.onPageChange}
 *     onPerPageChange={table.onPerPageChange} ... />
 *
 * Reglas de comportamiento:
 * - El fetch se dispara solo al cambiar `page`, `perPage` o cualquier valor que
 *   lea `params()`. No hay que llamar a nada a mano.
 * - Si cambian los filtros (`params()`), la página vuelve sola a la 1: el
 *   resultado de un filtro en la página 4 no se queda colgado en la 4.
 * - `refetch()` repite la petición actual, por ejemplo después de borrar una fila.
 * - `resetPage()` para volver a la primera página sin tocar filtros.
 * - El `fetcher` NO necesita `try/catch`: el hook captura el error, lo expone en
 *   `error()` y devuelve una lista vacía para que la tabla nunca se rompa.
 *
 * Cómo añadir paginación server-side a un listado nuevo:
 * 1. El service debe aceptar `page`/`limit` y devolver `{ items, meta: { total } }`
 *    (los listados del backend ya lo hacen).
 * 2. Sustituye el `createResource` + las señales `page`/`perPage` por este hook.
 * 3. Pasa sus props al `<Table>` y marca `serverSide`.
 * 4. La búsqueda y los filtros viajan en `params()`; el hook los observa y
 *    resetea la página. No repitas `setPage(1)` en cada `onSearch`/`onFilterChange`.
 */
export function useServerTable<T>(options: UseServerTableOptions<T>): ServerTableApi<T> {
  const [page, setPage] = createSignal(1)
  const [perPage, setPerPage] = createSignal(options.perPage ?? 15)
  /** El error del último fetch. Se limpia solo cuando uno vuelve a funcionar. */
  const [loadError, setLoadError] = createSignal<unknown>(undefined)

  const source = createMemo<Record<string, string | number | undefined>>(() => ({
    page: page(),
    limit: perPage(),
    ...options.params()
  }))

  const [resource, { refetch }] = createResource(source, async params => {
    try {
      const result = await options.fetcher(params)
      setLoadError(undefined)
      return result
    } catch (err: unknown) {
      setLoadError(err)
      return { items: [] as T[], meta: { total: 0 } }
    }
  })

  const data: Accessor<T[]> = () => resource()?.items ?? ([] as T[])
  const total: Accessor<number> = () => resource()?.meta.total ?? 0
  const loading: Accessor<boolean> = () => resource.loading
  const error: Accessor<unknown> = () => loadError() ?? resource.error

  /**
   * Cuando cambian los filtros (pero no la página ni las filas), volver a la 1.
   * `on(..., { defer: true })` evita disparar en el primer render. Se compara el
   * JSON de los params: un objeto nuevo con el mismo contenido no cuenta como
   * cambio (evita resets espurios si la página recrea el objeto en cada render).
   */
  let lastParamsKey = ''
  createEffect(
    on(
      () => options.params(),
      params => {
        const key = JSON.stringify(params)
        if (lastParamsKey && key !== lastParamsKey) setPage(1)
        lastParamsKey = key
      },
      { defer: true }
    )
  )

  const onPageChange = (event: PageChangeEventType) => setPage(event.page)
  const onPerPageChange = (event: PerPageChangeEventType) => setPerPage(event.perPage)

  return {
    data,
    total,
    loading,
    error,
    refetch,
    resetPage: () => setPage(1),
    page,
    perPage,
    onPageChange,
    onPerPageChange
  }
}
